import { FC } from 'react';
import styled, { keyframes } from 'styled-components';
import { EdnaWrapper } from '@providers/EdnaProvider';
import * as DialogPrimative from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import ClickableIcon from '@components/ClickableIcon';
import {
  ModalCloseProps,
  ModalContentProps,
  ModalProps,
  ModalTriggerProps,
} from './types';

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px 0 20px;
  height: 52px;
`;

const overlayShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 0.40;
  }
`;

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

type ContentProps = {
  $width?: string;
  $minWidth?: string;
  $maxWidth?: string;
};

const Content = styled(DialogPrimative.Content)<ContentProps>`
  background-color: white;
  z-index: 999999;
  border: 1px ${(props) => props.theme.colors.grey100} solid;
  border-radius: 8px;
  box-shadow: 0px 26px 80px 0px rgba(0, 0, 0, 0.2),
    0px 0px 1px 0px rgba(0, 0, 0, 0.2);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${(props) => props.$width};
  min-width: ${(props) => props.$minWidth};
  max-width: ${(props) => props.$maxWidth};
  animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
`;

const Divider = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.grey100};
`;

const Title = styled(DialogPrimative.Title)`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
`;

const Children = styled.div`
  padding: 14px 20px 20px 20px;
  margin: 0;
`;

const Overlay = styled(DialogPrimative.Overlay)`
  background-color: ${(props) => props.theme.colors.grey900};
  position: fixed;
  inset: 0;
  opacity: 0.4;
  z-index: 999999;
  animation: ${overlayShow} 300ms cubic-bezier(0.16, 1, 0.3, 1);
`;

/**
 * Modal
 *
 * A modal dialog that can be controlled or uncontrolled.
 *
 * Example:
 *
 * ```tsx
 * <Modal open={args.open} onOpenChange={args.onOpenChange}>
 *  <Modal.Trigger>
 *    <Button>Open Modal</Button>
 *  </Modal.Trigger>
 *  <Modal.Content title="My Modal">
 *   Some content here 💃 🕺 Some content here 💃 🕺 Some content here 💃 🕺
 *  Some content here 💃 🕺 Some content here 💃 🕺
 *  </Modal.Content>
 * </Modal>
 * ```
 *
 * @see {@link https://www.radix-ui.com/docs/primitives/components/dialog#dialog | Radix Dialog}
 */
const Modal: FC<ModalProps> & {
  Trigger: FC<ModalTriggerProps>;
  Content: FC<ModalContentProps>;
  Close: FC<ModalCloseProps>;
} = ({ children, onOpenChange, open, defaultOpen, modal }) => {
  return (
    <EdnaWrapper>
      <DialogPrimative.Root
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        modal={modal}
      >
        {children}
      </DialogPrimative.Root>
    </EdnaWrapper>
  );
};

/**
 * ModalTrigger
 *
 * The button that opens the dialog. Default comes with Trigger#asChild enabled.
 *
 * Example:
 *
 * ```tsx
 * <Modal.Trigger>
 *   <Button>Open Modal</Button>
 * </Modal.Trigger>
 * ```
 *
 * @see {@link https://www.radix-ui.com/docs/primitives/components/dialog#trigger | Radix Trigger}
 */
const ModalTrigger: FC<ModalTriggerProps> = ({ children }) => (
  <DialogPrimative.Trigger asChild>{children}</DialogPrimative.Trigger>
);

Modal.Trigger = ModalTrigger;

/**
 * Modal content
 *
 * Creates a modal dialog with a title, content, and close button.
 *  - Focus is automatically trapped when modal.
 *  - Manages screen reader announcements.
 *  - Esc closes the component automatically.
 *
 * Example:
 *
 * ```tsx
 * <Modal.Content title="My Modal">
 *  Some content here 💃 🕺 Some content here 💃 🕺 Some content here 💃 🕺
 * Some content here 💃 🕺 Some content here 💃 🕺
 * </Modal.Content>
 * ```
 *
 * @see {@link https://www.radix-ui.com/docs/primitives/components/dialog#portal | Radix Portal}
 * @see {@link https://www.radix-ui.com/docs/primitives/components/dialog#overlay | Radix Overlay}
 * @see {@link https://www.radix-ui.com/docs/primitives/components/dialog#content | Radix Content}
 * @see {@link https://www.radix-ui.com/docs/primitives/components/dialog#close | Radix Close}
 * @see {@link https://www.radix-ui.com/docs/primitives/components/dialog#Title | Radix Title}
 */
const ModalContent: FC<ModalContentProps> = ({
  title,
  children,
  width = '90vw',
  minWidth = '450px',
  maxWidth = '450px',
  className,
  ariaTitle,
}) => (
  <DialogPrimative.Portal>
    <Overlay />
    <Content
      $width={width}
      $minWidth={minWidth}
      $maxWidth={maxWidth}
      className={className}
      aria-describedby={undefined} // TODO: Make this available as a prop
    >
      {ariaTitle && (
        <VisuallyHidden.Root>
          <DialogPrimative.Title>{ariaTitle}</DialogPrimative.Title>
        </VisuallyHidden.Root>
      )}
      {title && (
        <>
          <HeaderContainer>
            <Title>{title}</Title>
            <DialogPrimative.Close asChild>
              <ClickableIcon name="X_CLOSE" />
            </DialogPrimative.Close>
          </HeaderContainer>
          <Divider />
        </>
      )}
      <Children>{children}</Children>
    </Content>
  </DialogPrimative.Portal>
);

Modal.Content = ModalContent;

/**
 * ModalClose
 *
 * The button that closes the dialog. Default comes with Close#asChild enabled.
 *
 * Example:
 *
 * ```tsx
 * <Modal.Close>
 *  <Button>Close Modal</Button>
 * </Modal.Close>
 * ```
 *
 * @see {@link https://www.radix-ui.com/docs/primitives/components/dialog#close | Radix Close}
 */
const ModalClose: FC<ModalCloseProps> = ({ children }) => (
  <DialogPrimative.Close asChild>{children}</DialogPrimative.Close>
);

Modal.Close = ModalClose;

export {
  ModalContent as Content,
  ModalTrigger as Trigger,
  ModalClose as Close,
};
export default Modal;
